import { FetchResponse, FetchService } from '@foundation-player/fetch-service';
import { Environment, SourceType } from '@foundation-player/runtime';
import { ClientAndScope } from '@foundation-player/types';
import { DeepPartial } from '@foundation-player/utils';
import { AdPlayerType, PlayerType } from '../builder-config';
import { FoundationGlobal } from '../foundation-global';

export type EntitlementConfig = {
  check?: {
    enable?: boolean;
    endpoint?: string;
  };
};

export type StreamingURLServiceConfig = DeepPartial<{
  endpoint?: string;
  enable?: boolean;
}>;

/**
 * WARN: We should use a deep partial here, because every key can be completely optional.
 *       Currently, this can be an issue, when using types in this interface. For example
 *       the `StreamingURLServiceConfig` type, has properties that aren't optional. That
 *       leaks in to the `RemoteConfig` and suddenly there is a mismatch between the
 *       actual data and the type representation. That's why we should use a `DeepPartial<>`
 *       here from the `@foundation-player/utils` package.
 */
export type RemoteConfig = DeepPartial<{
  bitmovinAnalytics?: {
    licenseKey?: string;
    enable?: boolean;
  };
  bitmovinPlayer?: {
    licenseKey?: string;
  };
  drm?: {
    appendDeviceHeaders?: boolean;
  };
  ssai?: {
    enable?: boolean;
    enableYospaceSdkForSourceTypes?: SourceType[];
    angBundle?: string;
    angRef?: string;
  };
  npawAnalytics?: {
    enable?: boolean;
    accountCode?: string;
    appName?: string;
  };
  npawCdnBalancer?: {
    enable?: boolean;
    enableForSourceTypes?: SourceType[];
    accountCode?: string;
    originCode?: string;
  };
  nielsen?: {
    enable?: boolean;
    endpoint?: string;
    clientId?: string;
    vcid?: string | { default?: string };
    apid?: string | { default?: string };
    publisherId?: string;
    debug?: boolean;
  };
  entitlement?: {
    liveevent?: EntitlementConfig;
    vod?: EntitlementConfig;
    livestream?: EntitlementConfig;
  };

  // We only differentiate between live and vod for the streamingURLService,
  // for details see here:
  // https://rtltech.atlassian.net/wiki/spaces/STECH/pages/145097206/Streaming+URL+Service+-+StUS+-+Runbook
  streamingURLService?: {
    [key in SourceType]?: StreamingURLServiceConfig;
  };

  concurrentStreamCheck?: {
    enable?: boolean;
    beatInterval?: number;
    startUrl?: string;
    heartbeatUrl?: string;
    stopUrl?: string;
  };

  advertisement?: {
    timeouts?: {
      atv?: number;
    };
  };

  heartbeat?: {
    enable?: boolean;

    offer?: string;

    videoService?: string;

    endpoints?: {
      [key in SourceType]?: string;
    };
  };

  /**
   * Optional builder configuration to explore the behavior of different settings for a bigger audience.
   */
  builderConfig?: {
    /**
     * Optionally override the player type.
     */
    playerType?: PlayerType;

    /**
     * Optionally override the ad player type.
     */
    adPlayerType?: AdPlayerType;
  };

  /**
   * Optional configuration for the Stream Fallback Plugin.
   */
  fallback?: {
    startup?: {
      /**
       * Enable the Stream Fallback Plugin for startup errors.
       */
      enable?: boolean;
    };
    inStream?: {
      /**
       * Enable the Stream Fallback Plugin for in-stream errors.
       */
      enable?: boolean;
    };
  };

  settings?: {
    abr?: {
      /**
       * Toggle bola abr setting if possible by the underlying player tech.
       */
      bola?: boolean;
    };
  };
}>;

function getEnvironmentDependentSubdomain(environment: Environment): string {
  switch (environment) {
    case Environment.Development:
      return 'player-dev';
    case Environment.PreProduction:
      return 'player-preprod';
    default:
      return 'player';
  }
}

export async function getRemoteConfig(
  clientAndScope: ClientAndScope,
  environment: Environment,
  fetchService = new FetchService(),
): Promise<RemoteConfig> {
  let url = `https://rc.${getEnvironmentDependentSubdomain(environment)}.streamingtech.de/config/html5/${clientAndScope.client}`;

  if ('scope' in clientAndScope && clientAndScope.scope) {
    url += `/${clientAndScope.scope}`;
  }

  const windowReference = window as unknown as FoundationGlobal;

  windowReference.foundation.internal_remoteConfig = {
    request: {
      time: Date.now(),
      url,
    },
  };

  try {
    const options = {
      // 5s is too large for any normal usage, but since a lot of our publishing partner sites,
      // are filled to the brim with ads and tracking scripts, we have to set it to a high value,
      // because unfortunately they refuse to start the player *after* the site is ready,
      // but instead do it alongside the bogged down site.
      timeout: 5000,
    };
    const response = await fetchService.get<RemoteConfig>(url, options);
    const body = await response.getBody();
    windowReference.foundation.internal_remoteConfig.response = {
      time: Date.now(),
      status: response.getStatus(),
      body,
    };
    windowReference.foundation.internal_remoteConfig.remoteConfig = body;
    return body;
  } catch (e) {
    let status = -1;
    let body = e;

    if (e instanceof FetchResponse) {
      status = e.getStatus();
      body = await e.getBody().catch(() => undefined);
    }
    windowReference.foundation.internal_remoteConfig.response = {
      time: Date.now(),
      status,
      body,
    };
    console.error(`Failed to fetch remote config from ${url}`, e);
  }

  return {};
}
